import React, { useEffect, useState } from 'react';
import { LoadingButton } from '@lf/ui-primitives';
import { createShare } from '@lf/api-client/lfApiShare';

function formatExpiresAt(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return d.toLocaleString();
    } catch (_) {
        return iso;
    }
}

const ShareStudyPlanDialog = ({ studyPlan, userProfile, isOpen, onClose }) => {
    const [share, setShare] = useState(null);
    const [error, setError] = useState(null);
    const [includeName, setIncludeName] = useState(false);
    const [copied, setCopied] = useState(false);
    const [isCreating, setIsCreating] = useState(false);

    useEffect(() => {
        if (!isOpen) {
            setShare(null);
            setError(null);
            setIncludeName(false);
            setCopied(false);
        }
    }, [isOpen]);

    if (!isOpen) return null;

    const handleGenerate = async () => {
        if (!studyPlan || !studyPlan.id) return;
        setIsCreating(true);
        setError(null);
        const sharerName = includeName && userProfile && userProfile.name
            ? userProfile.name
            : null;
        const result = await createShare(studyPlan.id, sharerName, setError);
        setIsCreating(false);
        if (result) {
            setShare(result);
        } else if (!error) {
            setError({ message: 'Could not generate share link. Please try again.' });
        }
    };

    const handleCopy = async () => {
        if (!share || !share.url) return;
        try {
            if (navigator.clipboard && navigator.clipboard.writeText) {
                await navigator.clipboard.writeText(share.url);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
            }
        } catch (_) {
            setCopied(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-6 rounded-lg shadow-lg w-[32rem] max-w-[90vw]">
                <h2 className="text-lg mb-4">Share study plan</h2>

                {studyPlan && (
                    <div className="mb-4 text-sm text-gray-700">
                        <div>
                            <span className="font-medium">{studyPlan.emoji || ''} {studyPlan.subject || 'Study plan'}</span>
                        </div>
                        {studyPlan.goal && (
                            <div className="text-gray-600 mt-1">{studyPlan.goal}</div>
                        )}
                    </div>
                )}

                <p className="text-sm text-gray-700 mb-3">
                    Anyone with this link can preview and import this plan.
                    They will not see your child's progress, watch history,
                    or chats.
                </p>

                {!share && (
                    <>
                        <label className="flex items-center gap-2 text-sm text-gray-700 mb-4">
                            <input
                                type="checkbox"
                                checked={includeName}
                                onChange={(e) => setIncludeName(e.target.checked)} />
                            Show my name on the share page (optional)
                        </label>

                        {error && (
                            <div className="text-red-500 text-sm mb-2">
                                {error.message || 'Could not generate share link.'}
                            </div>
                        )}

                        <div className="flex justify-end gap-2">
                            <LoadingButton
                                text="Generate share link"
                                isLoading={isCreating}
                                onClick={handleGenerate} />
                            <button
                                className="px-4 py-2 bg-blue-500 rounded text-white"
                                onClick={onClose}>
                                Cancel
                            </button>
                        </div>
                    </>
                )}

                {share && (
                    <>
                        <div className="mb-2">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Share link
                            </label>
                            <input
                                type="text"
                                readOnly
                                value={share.url}
                                className="w-full border border-gray-300 p-2 rounded text-sm"
                                onFocus={(e) => e.target.select()} />
                        </div>

                        <div className="flex items-center gap-2 mb-3">
                            <button
                                type="button"
                                className="bg-blue-500 text-white py-2 px-4 rounded"
                                onClick={handleCopy}>
                                Copy link
                            </button>
                            {copied && (
                                <span className="text-sm text-green-700">Copied</span>
                            )}
                        </div>

                        <p className="text-sm text-gray-600 mb-4">
                            This link expires in 7 days ({formatExpiresAt(share.expires_at)}).
                        </p>

                        <div className="flex justify-end gap-2">
                            <button
                                type="button"
                                className="px-4 py-2 bg-blue-500 rounded text-white"
                                onClick={onClose}>
                                Done
                            </button>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default ShareStudyPlanDialog;
